import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Card as ShadcnCard,
  CardHeader as ShadcnCardHeader,
  CardFooter as ShadcnCardFooter,
  CardTitle as ShadcnCardTitle,
  CardDescription as ShadcnCardDescription,
  CardContent as ShadcnCardContent,
} from "@/components/ui/card";

// --- NeoCard --- //
const NeoCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <ShadcnCard
    ref={ref}
    className={cn(
      "bg-card text-card-foreground rounded-none border-2 border-border shadow-[5px_5px_0px_0px_rgba(var(--foreground)/0.1)] hover:shadow-[8px_8px_0px_0px_rgba(var(--foreground)/0.15)] transition-shadow duration-200",
      className
    )}
    {...props}
  />
));
NeoCard.displayName = "NeoCard";

// --- NeoCardHeader --- //
const NeoCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <ShadcnCardHeader
    ref={ref}
    className={cn("p-4 border-b-2 border-border", className)} // Reduced padding, distinct border
    {...props}
  />
));
NeoCardHeader.displayName = "NeoCardHeader";

// --- NeoCardTitle --- //
const NeoCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <ShadcnCardTitle
    ref={ref}
    className={cn("text-2xl font-black uppercase tracking-wider text-accent", className)} // Neo-brutalist typography
    style={{fontFamily: '"Arial Black", sans-serif'}}
    {...props}
  />
));
NeoCardTitle.displayName = "NeoCardTitle";

// --- NeoCardDescription --- //
const NeoCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <ShadcnCardDescription
    ref={ref}
    className={cn("text-sm font-mono text-muted-foreground mt-1", className)} // Monospaced, muted
    {...props}
  />
));
NeoCardDescription.displayName = "NeoCardDescription";

// --- NeoCardContent --- //
const NeoCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <ShadcnCardContent ref={ref} className={cn("p-4 space-y-3", className)} {...props} /> // Standard padding for content
));
NeoCardContent.displayName = "NeoCardContent";

// --- NeoCardFooter --- //
const NeoCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <ShadcnCardFooter
    ref={ref}
    className={cn("p-4 border-t-2 border-dashed border-border flex items-center justify-end space-x-2", className)} // Dashed border, right-aligned items typical for actions
    {...props}
  />
));
NeoCardFooter.displayName = "NeoCardFooter";

export {
  NeoCard,
  NeoCardHeader,
  NeoCardFooter,
  NeoCardTitle,
  NeoCardDescription,
  NeoCardContent,
};
